import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TIMEZONE_OPTIONS,
  formatNow,
  getAllTimezoneOptions,
  timezoneLabel
} from '../utils/datetime';
import './TimezoneSelect.css';

const QUICK_ZONES = [
  'Europe/Belgrade',
  'Europe/London',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'UTC'
];

type TimezoneSelectProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  compact?: boolean;
};

const TimezoneSelect: React.FC<TimezoneSelectProps> = ({ value, onChange, disabled, id, compact }) => {
  const options = useMemo(() => getAllTimezoneOptions(), []);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedLabel = timezoneLabel(value);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const source = needle ? options : TIMEZONE_OPTIONS;
    if (!needle) {
      return source;
    }
    return options.filter(
      (zone) =>
        zone.label.toLowerCase().includes(needle) ||
        zone.value.toLowerCase().includes(needle)
    ).slice(0, 80);
  }, [options, query]);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    }
  }, [open]);

  const pick = (zone: string) => {
    onChange(zone);
    setOpen(false);
    setQuery('');
  };

  return (
    <div
      className={`tz-picker${compact ? ' compact' : ''}${open ? ' open' : ''}${disabled ? ' disabled' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        id={id}
        className="tz-picker-trigger"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span className="tz-picker-icon"><i className="pi pi-globe"></i></span>
        <span className="tz-picker-copy">
          <span className="tz-picker-city">{selectedLabel}</span>
          <span className="tz-picker-now">{formatNow(value)}</span>
        </span>
        <i className={`pi ${open ? 'pi-chevron-up' : 'pi-chevron-down'}`}></i>
      </button>

      {open && (
        <div className="tz-picker-panel">
          <input
            ref={searchRef}
            type="search"
            className="tz-picker-search"
            placeholder="Search city or timezone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          {!query && (
            <div className="tz-picker-chips">
              {QUICK_ZONES.map((zone) => (
                <button
                  key={zone}
                  type="button"
                  className={`tz-chip${value === zone ? ' active' : ''}`}
                  onClick={() => pick(zone)}
                >
                  {timezoneLabel(zone).split(' (')[0]}
                </button>
              ))}
            </div>
          )}
          <div className="tz-picker-list" role="listbox">
            {filtered.length === 0 ? (
              <div className="tz-picker-empty">No matching timezone</div>
            ) : (
              filtered.map((zone) => (
                <button
                  key={zone.value}
                  type="button"
                  role="option"
                  className={`tz-option${zone.value === value ? ' active' : ''}`}
                  onClick={() => pick(zone.value)}
                >
                  <span>{zone.label}</span>
                  {zone.value === value && <i className="pi pi-check"></i>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimezoneSelect;
